import { Check, PenNib } from "@phosphor-icons/react"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Badge } from "@tokenterminal/core/ui/messaging/badge/Badge"
import { BASE_PATH } from "@/constants/app.mjs"
import { productPageLogos } from "@/constants/productPageLogos"
import MarketingHeading from "../../../common/MarketingHeading"
import NicerBlockquote from "../../../common/NicerBlockquote"
import ProductPageHeader from "../../../products/ProductPageHeader"
import ProductPageTemplate from "../../../products/ProductPageTemplate"
import {
  CustomerLogoStrip,
  FeatureCard,
  StatsGrid,
  ThreeFeaturesSection,
} from "../../../products/shared"
import BuildComposableDashboards from "./BuildComposableDashboards"
import InteractiveChartEditorDemo from "./InteractiveChartEditorDemo"
import InteractiveQueryEditorDemo from "./InteractiveQueryEditorDemo"

const StudioPage = () => {
  return (
    <ProductPageTemplate
      header={
        <ProductPageHeader
          icon={PenNib}
          title="Studio"
          subtitle={
            <>
              No code chart and dashboard builder with
              <br />
              optional SQL access for technical users
            </>
          }
          ctaButtons={
            <>
              <LinkButton
                key="start-free"
                size="large"
                variant="solid"
                href="/explorer"
                style={{ zIndex: 4 }}
              >
                Start free
              </LinkButton>
              <LinkButton
                key="view-pricing"
                size="large"
                variant="ghost"
                href="/pricing"
                style={{ zIndex: 4 }}
              >
                View pricing
              </LinkButton>
            </>
          }
          backgroundImageUrl={`/${BASE_PATH}static/images/marketing/home/studio-illustration2.png`}
          whoWeServeText="Research teams and analysts who need custom visualizations - start without code, complement with SQL when ready."
          simpleWhoWeServeText="research teams and analysts"
        />
      }
      productId="studio"
    >
      {/* Customer Logo Strip */}
      <CustomerLogoStrip logos={productPageLogos.studio} />

      {/* Three Features Section */}
      <ThreeFeaturesSection
        features={[
          {
            component: (
              <FeatureCard>
                <InteractiveChartEditorDemo />
              </FeatureCard>
            ),
            title: "No-code chart creation",
            description:
              "Build any visualization with our no-code chart builder. Filter, group, and customize without writing a single query.",
          },
          {
            component: (
              <FeatureCard className="h-[300px]">
                <BuildComposableDashboards />
              </FeatureCard>
            ),
            title: "Feature-rich dashboards for research",
            description:
              "Combine charts into comprehensive dashboards. Share with your team or publish publicly.",
          },
          {
            component: (
              <FeatureCard className="h-[300px]">
                <InteractiveQueryEditorDemo />
              </FeatureCard>
            ),
            title: "SQL-based access for in-depth exploration",
            description:
              "Access our full data warehouse with SQL. Query 2+ petabytes of on-chain data directly.",
          },
        ]}
      />

      {/* Stats Grid */}
      <div className="mt-32">
        <StatsGrid
          stats={[
            {
              value: "100+",
              label: "Chains",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/ethereum.png`, alt: "Ethereum" },
                { src: `${BASE_PATH}/static/images/marketing/logos/solana.png`, alt: "Solana" },
                { src: `${BASE_PATH}/static/images/marketing/logos/base.png`, alt: "Base" },
                { src: `${BASE_PATH}/static/images/marketing/logos/tron.png`, alt: "Tron" },
              ],
            },
            {
              value: "1,200+",
              label: "Apps",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/aave.png`, alt: "Aave" },
                { src: `${BASE_PATH}/static/images/marketing/logos/uniswap.png`, alt: "Uniswap" },
                {
                  src: `${BASE_PATH}/static/images/marketing/logos/hyperliquid.png`,
                  alt: "Hyperliquid",
                },
                { src: `${BASE_PATH}/static/images/marketing/logos/sky.png`, alt: "Sky" },
              ],
            },
            {
              value: "7,000+",
              label: "Tokenized assets",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/usdt.png`, alt: "USDT" },
                { src: `${BASE_PATH}/static/images/marketing/logos/usdc.png`, alt: "USDC" },
                { src: `${BASE_PATH}/static/images/marketing/logos/blackrock.png`, alt: "BUIDL" },
                {
                  src: `${BASE_PATH}/static/images/marketing/logos/robinhood.png`,
                  alt: "Robinhood",
                },
              ],
            },
            {
              value: "2+ PB",
              label: "Of data",
              hasSparkline: true,
            },
          ]}
        />
      </div>

      {/* Customer Quote */}
      <div className="max-w-[900px] mx-auto mt-32">
        <NicerBlockquote
          content="As a crypto investor, I spend a lot of time trying to understand the financial performance of crypto protocols. Before Token Terminal, this meant a lot of manual work across different platforms, whereas now I'm able to access comparable metrics for hundreds of protocols all in one place."
          author="Sanat Kapur"
          authorTitle="Dragonfly Capital"
          authorImage={`/${BASE_PATH}static/images/marketing/home/testimonials/avatar-kapursanat.png`}
        />
      </div>

      {/* CTA Section */}
      <div className="max-w-[900px] mx-auto mt-72 mb-24">
        <div className="flex items-stretch gap-12 flex-col md:flex-row">
          <div>
            <div className="flex flex-col gap-5">
              <MarketingHeading level={2} className="text-2xl">
                Get started for free
              </MarketingHeading>
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-start gap-2">
                  <Icon as={Check} />
                  <div className="text-base text-fg-default">
                    Access dashboards for 1000s of projects
                  </div>
                </div>
                <div className="flex items-center justify-start gap-2">
                  <Icon as={Check} />
                  <div className="text-base text-fg-default">
                    Derive insights from prebuilt financial statements
                  </div>
                </div>
                <div className="flex items-center justify-start gap-2">
                  <Icon as={Check} />
                  <div className="text-base text-fg-default">
                    Create up to 3 charts and dashboards
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-start gap-2">
                <LinkButton className="w-full mt-4" size="large" href="/explorer">
                  Get started for free
                </LinkButton>
              </div>
            </div>
          </div>
          <div className="border-l border-border-default" style={{ width: "1px" }} />
          <div>
            <div className="flex flex-col gap-5">
              <div className="flex items-center gap-2">
                <Badge color="green" variant="solid" size="small" className="shrink-0">
                  PRO
                </Badge>
                <MarketingHeading level={2} className="text-2xl">
                  Build unlimited dashboards
                </MarketingHeading>
              </div>
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-start gap-2">
                  <Icon as={Check} />
                  <div className="text-base text-fg-default">
                    Create unlimited charts and dashboards
                  </div>
                </div>
                <div className="flex items-center justify-start gap-2">
                  <Icon as={Check} />
                  <div className="text-base text-fg-default">Download your charts and data</div>
                </div>
                <div className="flex items-center justify-start gap-2">
                  <Icon as={Check} />
                  <div className="text-base text-fg-default">Collaborate with your team</div>
                </div>
              </div>
              <div className="flex items-center justify-start gap-2">
                <LinkButton className="w-full mt-4" size="large" href="/pricing" color="success">
                  Subscribe to Pro
                </LinkButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProductPageTemplate>
  )
}

export default StudioPage
